/* eslint-disable max-len */
import { ComponentType, FC } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { ConnectedComponent } from 'react-redux';
import generateAsyncRouteComponent from '../components/AsyncComponent';

interface DefaultModule<T> {
  default: T;
}
const getDefaultExport = <T>(module: DefaultModule<T>): T => module.default;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AsyncRouteComponent = ComponentType<any> & {
  load: () => Promise<void>;
};

export interface Route {
  path: string;
  Component: AsyncRouteComponent;
  exact?: boolean;
}

export enum InstrumentKind {
  bass = 'bass',
  guitar = 'guitar',
  ukulele = 'ukulele',
}

export function isInstrumentKind(instStr?: string | null): instStr is InstrumentKind {
  return !!instStr && instStr in InstrumentKind;
}

export interface Instrument {
  instrument: InstrumentKind;
}

export interface BrowseSkillsParams extends Instrument {
  skillType: string;
  offset: string;
}

export interface BrowseSongsParams extends Instrument {
  type: string;
  genre: string;
}

interface InjectedIntlFake {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  intl: any;
}

export type InstrumentSkillsProps = RouteComponentProps<Instrument> & InjectedIntlFake;
export type InstrumentSongsProps = InstrumentSkillsProps;
export type InstrumentCollectionsProps = InstrumentSkillsProps;

export type BrowseSkillsRouteProps = RouteComponentProps<BrowseSkillsParams>;
export type BrowseSongsRouteProps = RouteComponentProps<BrowseSongsParams>;

export interface LessonsParams {
  lessonSlug: string;
}
export interface PagesParams {
  slug: string;
}
export interface PlansParams {
  plan: string;
}

export interface PracticeParams {
  practiceSlug: string;
}

export type LessonsRouteProps = RouteComponentProps<LessonsParams>;
export type PagesRouteProps = Partial<RouteComponentProps<PagesParams>>;
export type PlansRouteProps = RouteComponentProps<PlansParams>;
export type PracticeRouteProps = RouteComponentProps<PracticeParams>;
// Lessons route is wrapped in connect
type PagesRouteComponent = ConnectedComponent<ComponentType<PagesRouteProps>, PagesRouteProps>;

export interface PathParams {
  level: string;
}

type PathRouteParams = RouteComponentProps<PathParams>;
type PathRouteComponent = ConnectedComponent<ComponentType<PathRouteParams>, PathRouteParams>;

// The top-level routes
const routes: Route[] = [
  {
    path: '/',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType<any>> =>
        import(/* webpackChunkName: "HomeRoute" */ './home/home-router').then(getDefaultExport),
    }),
  },
  {
    path: '/songs',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "SongsRoute" */ './songs/components/SongsContainer').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/admin/video-preview/:videoId',
    Component: generateAsyncRouteComponent<{}, ComponentType<{}>>({
      loader: (): Promise<ComponentType> =>
        import(
          /* webpackChunkName: "AdminRoute" */ './admin/components/VideoPreviewContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/articles',
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "ArticlesRoute" */ './articles/components/ArticlesFeed').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/bundles-ineligible',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "BundlesIneligible" */ './bundles-ineligible').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/courses/:courseSlug',
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType<any>> =>
        import(/* webpackChunkName: "PublicCourseRoute" */ './public-courses').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/favorites',
    exact: true,
    Component: generateAsyncRouteComponent<{}, ComponentType<{}>>({
      loader: (): Promise<ComponentType<any>> =>
        import(
          /* webpackChunkName: "InstrumentSkillsRoute" */ './favorites/FavoritesContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/:instrument(bass|guitar|ukulele)/collections',
    exact: true,
    Component: generateAsyncRouteComponent<
      InstrumentCollectionsProps,
      FC<InstrumentCollectionsProps>
    >({
      loader: (): Promise<FC<InstrumentCollectionsProps>> =>
        import(
          /* webpackChunkName: "InstrumentCollectionsRoute" */ './collections/CollectionsLanding'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/:instrument(bass|guitar|ukulele)/skills',
    exact: true,
    Component: generateAsyncRouteComponent<InstrumentSkillsProps, FC<InstrumentSkillsProps>>({
      loader: (): Promise<FC<InstrumentSkillsProps>> =>
        import(/* webpackChunkName: "InstrumentSkillsRoute" */ './instrument/Skills').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/chord-challenge',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(
          /* webpackChunkName: "ChordChallengeLandingRoute" */ './chord-challenge/ChordChallengeLanding'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/chord-challenge/transitions',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(
          /* webpackChunkName: "ChordChallengeProgressionRoute" */ './chord-challenge/ChordChallengeProgressions'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/chord-challenge/session/:transitionKey',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(
          /* webpackChunkName: "ChordChallengeSessionRouter" */ './chord-challenge/ChordChallengeSession'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/chord-challenge/session',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(
          /* webpackChunkName: "ChordChallengeSessionRouter" */ './chord-challenge/ChordChallengeSession'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/:instrument(bass|guitar|ukulele)/songs',
    exact: true,
    Component: generateAsyncRouteComponent<InstrumentSongsProps, FC<InstrumentSongsProps>>({
      loader: (): Promise<FC<InstrumentSongsProps>> =>
        import(/* webpackChunkName: "InstrumentSongsRoute" */ './instrument/Songs').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/:instrument(bass|guitar|ukulele)/skills/:skillType?/:offset?',
    Component: generateAsyncRouteComponent<BrowseSkillsRouteProps, FC<BrowseSkillsRouteProps>>({
      loader: (): Promise<FC<BrowseSkillsRouteProps>> =>
        import(
          /* webpackChunkName: "instrumentSkillsTypeRoute" */ './instrument/BrowseSkills'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/:instrument(bass|guitar|ukulele)/:type/:level/:genre?/:offset?',
    Component: generateAsyncRouteComponent<BrowseSongsRouteProps, FC<BrowseSongsRouteProps>>({
      loader: (): Promise<FC<BrowseSongsRouteProps>> =>
        import(
          /* webpackChunkName: "instrumentArtistsTypeRoute" */ './instrument/BrowseSongs'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/my-progress:type?',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(
          /* webpackChunkName: "myProgressRoute" */ './my-progress/components/MyProgressContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/my-progress/courses',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(
          /* webpackChunkName: "myProgressCoursesRoute" */ './my-progress/components/CourseProgressContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/my-progress/:type',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType<any>> =>
        import(
          /* webpackChunkName: "myProgressLessonsRoute" */ './my-progress/components/LessonsProgressContainer'
        ).then(getDefaultExport),
    }),
  },
  {
    path: '/lessons/:lessonSlug',
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType<any>> =>
        import(/* webpackChunkName: "PublicLessonRoute" */ './public-lessons').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/pages/:slug',
    exact: true,
    Component: generateAsyncRouteComponent<PagesRouteProps, PagesRouteComponent>({
      loader: (): Promise<PagesRouteComponent> =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        import(/* webpackChunkName: "pagesRoute" */ './pages/components/Pages').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/path',
    exact: true,
    Component: generateAsyncRouteComponent<PathRouteParams, PathRouteComponent>({
      loader: (): Promise<PathRouteComponent> =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore TODO SubscriptionCheck needs work
        import(/* webpackChunkName: "pathRoute" */ './path/components/PathContainer').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/path/:level?',
    exact: true,
    Component: generateAsyncRouteComponent<PathRouteParams, PathRouteComponent>({
      loader: (): Promise<PathRouteComponent> =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore TODO SubscriptionCheck needs work
        import(/* webpackChunkName: "pathRoute" */ './path/components/PathContainer').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/paths/:path?',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "pathsRoute" */ './paths/path/components/PathContainer').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/plans/:plan?',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        import(/* webpackChunkName: "plansRoute" */ './plans/plans-router').then(getDefaultExport),
    }),
  },
  {
    path: '/checkout',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        import(/* webpackChunkName: "checkoutRoute" */ './checkout/components/Checkout').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/quiz',
    exact: true,
    Component: generateAsyncRouteComponent<{}, ComponentType<{}>>({
      loader: (): Promise<ComponentType<{}>> =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        import(/* webpackChunkName: "quizRoute" */ './quiz/components/Quiz').then(getDefaultExport),
    }),
  },
  {
    path: '/restricted',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "restrictedRoute" */ './restricted/components/Restricted').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/redeem',
    exact: true,
    Component: generateAsyncRouteComponent<RouteComponentProps, ComponentType<RouteComponentProps>>(
      {
        loader: (): Promise<ComponentType<RouteComponentProps>> =>
          import(/* webpackChunkName: "redeemRoute" */ './redeem/components/Redeem').then(
            getDefaultExport,
          ),
      },
    ),
  },
  {
    path: '/redeem/success',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "redeemRoute" */ './redeem/components/RedeemSuccess').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/redeem/error',
    exact: true,
    Component: generateAsyncRouteComponent({
      loader: (): Promise<ComponentType> =>
        import(/* webpackChunkName: "redeemRoute" */ './redeem/components/RedeemError').then(
          getDefaultExport,
        ),
    }),
  },
  {
    path: '/search',
    exact: true,
    Component: generateAsyncRouteComponent<RouteComponentProps, ComponentType<RouteComponentProps>>(
      {
        loader: (): Promise<ComponentType<RouteComponentProps>> =>
          import(/* webpackChunkName: "searchRoute" */ './search/search-wrapper').then(
            getDefaultExport,
          ),
      },
    ),
  },
  {
    path: '/search/results',
    exact: true,
    Component: generateAsyncRouteComponent<RouteComponentProps, ComponentType<RouteComponentProps>>(
      {
        loader: (): Promise<ComponentType<RouteComponentProps>> =>
          import(/* webpackChunkName: "searchResultsRoute" */ './search/results-wrapper').then(
            getDefaultExport,
          ),
      },
    ),
  },
  {
    path: '/tab-viewer',
    exact: true,
    Component: generateAsyncRouteComponent<RouteComponentProps, ComponentType<RouteComponentProps>>(
      {
        loader: (): Promise<ComponentType<RouteComponentProps>> =>
          import(/* webpackChunkName: "tabViewerRoute" */ './tab-viewer/tab-wrapper').then(
            getDefaultExport,
          ),
      },
    ),
  },
  {
    path: '/up-next',
    exact: true,
    Component: generateAsyncRouteComponent<RouteComponentProps, ComponentType<RouteComponentProps>>(
      {
        loader: (): Promise<ComponentType<RouteComponentProps>> =>
          import(/* webpackChunkName: "upNextRoute" */ './up-next').then(getDefaultExport),
      },
    ),
  },
  {
    path: '/welcome-screen',
    exact: true,
    Component: generateAsyncRouteComponent<{}, ComponentType<{}>>({
      loader: (): Promise<ComponentType<{}>> =>
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        import(
          /* webpackChunkName: "welcomeScreenRoute" */ './welcome-screen/components/WelcomeScreen'
        ).then(getDefaultExport),
    }),
  },
];

export default routes;
